import { useState } from "react";
import { motion } from "framer-motion";
import { BENEFIT_GROUPS, ENGINES, FEATURES } from "../../constants/verify";
import { CornerMarks, MediaSlot, SectionShell, useFade } from "./ui";

function VerifyEngines() {
  const { fade } = useFade();
  const [engineId, setEngineId] = useState(ENGINES[0].id);
  const [audience, setAudience] = useState(BENEFIT_GROUPS[0].id);
  const engine = ENGINES.find((item) => item.id === engineId) || ENGINES[0];
  const benefit = BENEFIT_GROUPS.find((item) => item.id === audience) || BENEFIT_GROUPS[0];

  return (
    <>
      <SectionShell id="engines" labelledBy="engines-heading" tone="ice">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Core verification services
            </p>
            <h2
              id="engines-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Four engines.
              <span className="mt-1 block text-verify-royal">One trust picture.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Open an engine to see the checks it runs. Vehicle, device, SIM behaviour and AI trust
              scoring work together — not as four disconnected products.
            </p>
          </motion.header>

          <div className="mt-12 grid items-stretch gap-4 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)]">
            <ol className="flex h-full flex-col border border-navy/10 bg-white">
              {ENGINES.map((item) => {
                const on = item.id === engine.id;
                return (
                  <li key={item.id} className="flex min-h-0 flex-1 border-t border-navy/8 first:border-t-0">
                    <button
                      type="button"
                      onClick={() => setEngineId(item.id)}
                      className={`flex h-full min-h-[4.75rem] w-full cursor-pointer items-center gap-4 px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-verify-gold ${
                        on
                          ? "border-l-[3px] border-verify-royal bg-[#eef3fb]"
                          : "border-l-[3px] border-transparent hover:bg-[#eef3fb]"
                      }`}
                      aria-pressed={on}
                    >
                      <span className={`font-mono text-[11px] tracking-[0.16em] ${on ? "text-verify-royal" : "text-navy/35"}`}>
                        {item.n}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-navy/40">
                          {item.kicker}
                        </span>
                        <span className="mt-1 block text-sm font-semibold leading-snug tracking-tight text-navy">
                          {item.title}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="relative flex h-full min-h-[28rem] flex-col border border-navy/10 bg-white">
              <CornerMarks />
              <div className="relative min-h-[12rem] overflow-hidden border-b border-navy/10 bg-navy lg:min-h-[14rem]">
                <MediaSlot
                  key={engine.id}
                  src={engine.art}
                  alt={engine.alt}
                  label={engine.title}
                  spec={engine.spec}
                  className="absolute inset-0 h-full w-full"
                  tone="dark"
                />
              </div>
              <div className="flex flex-1 flex-col p-6 sm:p-8">
                <p className="font-mono text-[11px] tracking-[0.2em] text-verify-royal">
                  ENGINE {engine.n} · {engine.kicker.toUpperCase()}
                </p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy sm:text-3xl">{engine.title}</h3>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-navy/60">{engine.copy}</p>
                <ul className="mt-6 grid flex-1 auto-rows-fr gap-2 sm:grid-cols-2">
                  {engine.items.map((item) => (
                    <li
                      key={item}
                      className="flex h-full items-start gap-3 border-l-[3px] border-verify-gold bg-[#eef3fb] px-3 py-3 text-sm font-semibold leading-snug text-navy"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-verify-green" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="features" labelledBy="features-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Key features
            </p>
            <h2
              id="features-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Enterprise capabilities
              <span className="mt-1 block">on one platform</span>
            </h2>
          </motion.header>

          <ol className="mt-12 grid auto-rows-fr gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {FEATURES.map((item, index) => (
              <li key={item} className="flex h-full min-h-[7.5rem] flex-col bg-white p-4">
                <span className="font-mono text-[11px] tracking-[0.14em] text-verify-cyan">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p className="mt-3 text-sm font-semibold leading-snug tracking-tight text-navy">{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </SectionShell>

      <SectionShell id="benefits" labelledBy="benefits-heading" tone="ice">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Features &amp; benefits
            </p>
            <h2
              id="benefits-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Trust, by the desk
              <span className="mt-1 block">that uses it</span>
            </h2>
          </motion.header>

          <div className="mt-12 border border-navy/10 bg-white">
            <div className="flex gap-1 overflow-x-auto border-b border-navy/10 p-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {BENEFIT_GROUPS.map((item) => {
                const on = item.id === benefit.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setAudience(item.id)}
                    className={`inline-flex min-h-11 shrink-0 cursor-pointer items-center whitespace-nowrap px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-verify-gold ${
                      on ? "bg-verify-royal text-white" : "text-navy/65 hover:bg-[#eef3fb] hover:text-navy"
                    }`}
                    aria-pressed={on}
                  >
                    {item.title}
                  </button>
                );
              })}
            </div>
            <div className="grid items-stretch lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="flex flex-col justify-between border-b border-navy/10 p-6 sm:p-8 lg:border-b-0 lg:border-r">
                <p className="font-mono text-[11px] tracking-[0.2em] text-verify-royal">{benefit.n}</p>
                <h3 className="mt-4 text-2xl font-semibold tracking-tight text-navy">{benefit.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-navy/60">{benefit.copy}</p>
              </div>
              <ul className="grid auto-rows-fr gap-px bg-navy/10 sm:grid-cols-2">
                {benefit.items.map((item) => (
                  <li key={item} className="flex h-full items-start gap-3 bg-white px-5 py-4 text-sm font-semibold leading-snug text-navy">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-verify-green" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </SectionShell>
    </>
  );
}

export default VerifyEngines;
